import {useCallback, useEffect, useState} from 'react';

interface Settled<T> {
  key: string;
  data: T | undefined;
  error: Error | undefined;
}

/**
 * Runs `request` for the current `args`, with Apollo's result names. A change to `args`, or a
 * `refetch`, starts a new request and drops any answer to an older one. `data` stays on the last
 * answer while the next one loads.
 */
export const useAsyncRequest = <A, T>(request: (args: A) => Promise<T>, args: A) => {
  const [reloads, setReloads] = useState(0);
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const argsKey = JSON.stringify(args);
  const key = `${argsKey}|${reloads}`;

  useEffect(() => {
    let isCurrent = true;
    request(JSON.parse(argsKey) as A).then(
      (data) => {
        if (isCurrent) setSettled({key, data, error: undefined});
      },
      (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        if (isCurrent) setSettled((previous) => ({key, data: previous?.data, error}));
      }
    );
    return () => {
      isCurrent = false;
    };
  }, [request, argsKey, key]);

  const refetch = useCallback(() => setReloads((n) => n + 1), []);
  const isSettled = settled?.key === key;

  return {
    data: settled?.data,
    loading: !isSettled,
    error: isSettled ? settled.error : undefined,
    refetch
  };
};
